import { useState } from 'react';
import { InputField } from '@clay-run/glaze/input-field';
import { HEALTH_KEYS, RENEWAL_PRESETS, renewalRangeError, type HealthKey, type RenewalFilter, type RenewalRangeError, type RenewalPreset } from '@shared/flow/account-hierarchy-filters';
import { MultiSelectField, SingleSelectField } from './SidebarFields';
import { FILTER_BAR_COPY, HEALTH_LABEL, RENEWAL_PRESET_LABEL, healthSelectionLabel } from './hierarchyPresentation';
import { AUTOFILL_IGNORE_PROPS } from '../../../../lib/autofill';

export interface RenewalsFiltersProps {
  renewal: RenewalFilter;
  onRenewalChange: (next: RenewalFilter) => void;
  health: readonly HealthKey[];
  onHealthChange: (next: HealthKey[]) => void;
}

const PRESET_ITEMS = RENEWAL_PRESETS.map(p => ({ value: p, label: p === 'custom' ? FILTER_BAR_COPY.customRange : RENEWAL_PRESET_LABEL[p] }));
const HEALTH_OPTIONS = HEALTH_KEYS.map(k => ({ key: k, label: HEALTH_LABEL[k] }));

/** The Renewals section: Renewal date (presets; Custom range reveals From / To) and Health. */
export function RenewalsFilters({ renewal, onRenewalChange, health, onHealthChange }: RenewalsFiltersProps) {
  // An invalid or inverted range is shown (with its error) but never committed: the last valid filter
  // keeps applying. `base` ties the draft to the committed values, so an outside reset discards it.
  const [draft, setDraft] = useState<{ from: string | null; to: string | null; error: RenewalRangeError; baseFrom: string | null; baseTo: string | null } | null>(null);
  const liveDraft = draft && draft.baseFrom === renewal.from && draft.baseTo === renewal.to ? draft : null;
  const shownFrom = liveDraft ? liveDraft.from : renewal.from;
  const shownTo = liveDraft ? liveDraft.to : renewal.to;
  const error = liveDraft?.error ?? null;
  const editRange = (from: string | null, to: string | null) => {
    const bad = renewalRangeError(from, to);
    if (bad) {
      setDraft({ from, to, error: bad, baseFrom: renewal.from, baseTo: renewal.to });
      return;
    }
    setDraft(null);
    onRenewalChange({ ...renewal, from, to });
  };
  return (
    <>
      <SingleSelectField<RenewalPreset>
        label={FILTER_BAR_COPY.renewalDateLabel}
        value={renewal.preset}
        items={PRESET_ITEMS}
        onChange={preset => onRenewalChange({ ...renewal, preset })}
      />
      {renewal.preset === 'custom' && (
        <div className="flex flex-col gap-1">
        <div className="grid grid-cols-2 gap-2">
          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            type="date"
            size="sm"
            label={FILTER_BAR_COPY.customFrom}
            aria-label="Renewal date from"
            value={shownFrom ?? ''}
            invalid={error === 'invalid_from' || error === 'inverted'}
            onChange={e => editRange(e.target.value || null, shownTo)}
          />
          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            type="date"
            size="sm"
            label={FILTER_BAR_COPY.customTo}
            aria-label="Renewal date to"
            value={shownTo ?? ''}
            invalid={error === 'invalid_to' || error === 'inverted'}
            onChange={e => editRange(shownFrom, e.target.value || null)}
          />
        </div>
        {error && <p role="alert" className="text-xs text-content-danger">{error === 'inverted' ? FILTER_BAR_COPY.rangeInverted : FILTER_BAR_COPY.rangeInvalidDate}</p>}
        </div>
      )}
      <MultiSelectField
        label={FILTER_BAR_COPY.healthLabel}
        summary={healthSelectionLabel(health)}
        active={health.length > 0}
        options={HEALTH_OPTIONS}
        isChecked={key => health.some(k => k === key)}
        onToggle={key => onHealthChange(HEALTH_KEYS.filter(k => (k === key ? !health.includes(k) : health.includes(k))))}
      />
    </>
  );
}
